import { AxiosError } from 'axios'
import MockAdapter from 'axios-mock-adapter'
import client from '@/client'
import { isLocalDbEnabled, LOCAL_DB_LATENCY_MS } from './config'
import { currentUserId } from './engine/context'
import { registerAuthHandlers, signInPersona } from './handlers/auth.handler'
import { registerClaimFgHandlers } from './handlers/claimFg.handler'
import { registerCommandCenterHandlers } from './handlers/commandCenter'
import { registerDestructionFgHandlers } from './handlers/destructionFg.handler'
import { registerDestructionRmHandlers } from './handlers/destructionRm.handler'
import { registerFgInboundHandlers } from './handlers/fgInbound.handler'
import { registerInboundHandlers } from './handlers/inbound.handler'
import { registerInboundRmHandlers } from './handlers/inboundRm.handler'
import { registerInboundRmOperatorHandlers } from './handlers/inboundRmOperator.handler'
import { registerInventoryHandlers } from './handlers/inventory.handler'
import { registerItemsHandlers } from './handlers/items.handler'
import { registerMasterDataHandlers } from './handlers/masterData.handler'
import { registerMaterialRequestHandlers } from './handlers/materialRequest.handler'
import { registerMergeConsolidationHandlers } from './handlers/mergeConsolidation.handler'
import { registerNgHandlingHandlers } from './handlers/ngHandling.handler'
import { registerNotificationHandlers } from './handlers/notification.handler'
import { opnameLockRejection, registerOpnameHandlers } from './handlers/opname.handler'
import { registerOutboundFgHandlers } from './handlers/outboundFg.handler'
import { registerPendataanAwalHandlers } from './handlers/pendataanAwal.handler'
import { registerReturnOrderHandlers } from './handlers/returnOrder.handler'
import { registerTransferHandlers } from './handlers/transfer.handler'
import { registerWarehouseSetupHandlers } from './handlers/warehouseSetup.handler'
import { registerWipReturnHandlers } from './handlers/wipReturn.handler'
import { registerWmsHandlers } from './handlers/wms.handler'
import { registerWorkflowHandlers } from './handlers/workflow.handler'
import { LOCAL_DB_SCHEMA } from './schema'
import { buildLocalDbSeed, LOCAL_DB_PERSONAS, LOCAL_DB_SEED_VERSION } from './seed'
import { applySeed, readMeta, readTable } from './store/localDb.store'

/**
 * Local mode MVDP: seluruh request `client.api` dilayani di browser dari database jotai + localStorage.
 * Service di modul tetap memanggil endpoint asli (Directus `/items`, `/api/workflow/submission/v2`,
 * custom `/api/v1/...`) sehingga saat backend siap cukup mematikan `NEXT_PUBLIC_LOCAL_DB`.
 */

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

export function currentRoleId(): string {
  const privilege = readTable('mt_privileges').find(row => idOf(row.user) === currentUserId())
  return privilege ? idOf(privilege.role) : ''
}

export function resetLocalDb(): void {
  applySeed(buildLocalDbSeed(), LOCAL_DB_SEED_VERSION)
}

export function switchPersona(userId: string): void {
  signInPersona(userId)
}

function ensureSeeded() {
  if (readMeta()?.seedVersion !== LOCAL_DB_SEED_VERSION) resetLocalDb()
}

function ensurePersona() {
  const persona = currentUserId()
  if (!persona || !readTable('directus_users').some(user => user.id === persona)) {
    signInPersona(LOCAL_DB_PERSONAS[0].id)
  }
}

let installed = false

export function installLocalDb(): void {
  if (installed || typeof window === 'undefined' || !isLocalDbEnabled()) return
  installed = true

  ensureSeeded()
  ensurePersona()

  /** Kunci lokasi yang sedang dihitung opname (OPN-03): 423. Pendataan Awal v7.50 tidak membekukan gudang. */
  client.api.interceptors.request.use(config => {
    const message = opnameLockRejection(config.method, config.url, config.data)
    if (!message) return config
    const response = {
      status: 423,
      statusText: 'Locked',
      headers: {},
      config,
      data: { statusCode: 423, code: 'ERR_WAREHOUSE_FROZEN', message }
    }
    return Promise.reject(new AxiosError(message, 'ERR_BAD_REQUEST', config, null, response))
  })

  const mock = new MockAdapter(client.api, { delayResponse: LOCAL_DB_LATENCY_MS })
  registerAuthHandlers(mock)
  registerWorkflowHandlers(mock, currentRoleId)
  registerWmsHandlers(mock)
  registerInboundHandlers(mock)
  registerInboundRmHandlers(mock)
  registerInboundRmOperatorHandlers(mock)
  registerReturnOrderHandlers(mock)
  registerMaterialRequestHandlers(mock)
  registerTransferHandlers(mock)
  registerWipReturnHandlers(mock)
  registerFgInboundHandlers(mock)
  registerOutboundFgHandlers(mock)
  registerMergeConsolidationHandlers(mock)
  registerClaimFgHandlers(mock)
  registerWarehouseSetupHandlers(mock)
  registerPendataanAwalHandlers(mock)
  registerInventoryHandlers(mock)
  registerDestructionFgHandlers(mock)
  registerDestructionRmHandlers(mock)
  registerNgHandlingHandlers(mock)
  registerOpnameHandlers(mock)
  registerMasterDataHandlers(mock)
  registerNotificationHandlers(mock)
  registerCommandCenterHandlers(mock)
  registerItemsHandlers(mock, LOCAL_DB_SCHEMA)
  /** Directus `/notifications` (bell header modul non-WMS) — tidak dipakai WMS; WMS memakai `/api/v1/wms/notifications`. */
  mock.onGet('/notifications').reply(200, { data: [] })
  mock.onAny().reply(config => {
    console.warn(`[local-db] Endpoint belum diemulasikan: ${config.method?.toUpperCase()} ${config.url}`)
    return [404, { message: `Endpoint ${config.url} belum tersedia di local mode.` }]
  })
}

installLocalDb()
